import { ApiType } from "@gymflow/common";
import {
  EventStatisticField,
  GlobalStatisticDTO,
  GlobalStatisticsCategories,
  GlobalStatisticTrendPeriod,
  LeadStatisticField,
  MembershipStatisticField,
  RevenueStatisticField,
  VisitStatisticField,
} from "@gymflow/types";
import * as Sentry from "@sentry/react";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { statisticQueryKeys } from "./statisticQueryKeys";

export function useStatistic(
  {
    api,
    clubId,
    start,
    end,
    trendPeriod,
    categories,
    revenueFields,
    membershipFields,
    eventFields,
    leadFields,
    visitFields,
    membershipIncluded,
    membershipExcluded,
  }: {
    api: ApiType;
    clubId?: number;
    start?: string;
    end?: string;
    trendPeriod?: GlobalStatisticTrendPeriod;
    categories: GlobalStatisticsCategories[];
    revenueFields?: RevenueStatisticField[];
    membershipFields?: MembershipStatisticField[];
    eventFields?: EventStatisticField[];
    leadFields?: LeadStatisticField[];
    visitFields?: VisitStatisticField[];
    membershipIncluded?: number[];
    membershipExcluded?: number[];
  },
  queryOpts?: Omit<UseQueryOptions<GlobalStatisticDTO | null>, "queryKey">,
) {
  return useQuery({
    queryKey: statisticQueryKeys.statistic({
      clubId,
      start,
      end,
      trendPeriod,
      categories,
      revenueFields,
      membershipFields,
      eventFields,
      leadFields,
      visitFields,
      membershipIncluded,
      membershipExcluded,
    }),
    queryFn: async () => {
      try {
        const result = await api.statisticsApi.getData(
          clubId!,
          start!,
          end!,
          trendPeriod,
          categories,
          revenueFields,
          membershipFields,
          eventFields,
          leadFields,
          visitFields,
          membershipIncluded,
          membershipExcluded,
        );
        return result.data;
      } catch (error) {
        Sentry.captureMessage(
          `useStatistic called with ${JSON.stringify({
            start,
            end,
            trendPeriod,
            categories,
            revenueFields,
            membershipFields,
            eventFields,
            leadFields,
            visitFields,
            membershipIncluded,
            membershipExcluded,
          })}`,
        );
        Sentry.captureException(error);
        throw error;
      }
    },
    enabled:
      !!clubId && !!start && !!end && !!categories && categories.length > 0,
    ...queryOpts,
  });
}
